import { Fragment, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  BadgeModulo,
  ContainerItems,
  DivisorMenu,
  IconeMenu,
  ItemBloqueado,
  LinkStyled,
  LogoMenu,
  MenuStyle,
  NavegacaoStyle,
  SecaoRotuloStyle,
  SecaoTituloStyle,
  SetaSecao,
} from './Menu.style'
import { PieTwoIcon } from '../../assets/icons/pie-two'
import { ChartLineIcon } from '../../assets/icons/chart-line'
import { CalendarIcon } from '../../assets/icons/calendar'
import { FacialCleanserIcon } from '../../assets/icons/facial-cleanser'
import { IconDollar } from '../../assets/icons/dollar'
import { IconCity } from '../../assets/icons/cityWhite'
import { IconCard } from '../../assets/icons/id-card-h'
import { IconFiles } from '../../assets/icons/file-doc'
import { EveryUser } from '../../assets/icons/every-user'
import { AdProduct } from '../../assets/icons/ad-product'
import { TrendingUpIcon } from '../../assets/icons/trending-up'
import { ChevronDownIcon } from '../../assets/icons/chevron down'
import { ModuloMixIcon } from '../../assets/icons/modulo-mix'
import { ModuloRadarIcon } from '../../assets/icons/modulo-radar'
import { SecaoDadosIcon } from '../../assets/icons/secao-dados'
import { SecaoAdministracaoIcon } from '../../assets/icons/secao-administracao'
import Status from '../../assets/icons/status'
import Organization from '../../assets/icons/organization'
import { colors } from '../../theme'
import { useTopUserData } from '../Top/TopUserData/useTopUserData'
import { LogoCartivia } from '../LogoCartivia'
import { UserIcon } from '../../assets/icons/user'
import Undo from '../../assets/icons/undo'
import { SettingTwoIcon } from '../../assets/icons/setting-two'
import { MessagesIcon } from '../../assets/icons/messages'
import { ehRotaAdministrativa } from '../../utils/rotaAdministrativa'

type MenuProps = {
  isBigMenu: boolean
}

type ItemMenu = {
  url: string
  icon: React.ReactNode
  text: string
  /**
   * Segmentos de `pathname` que acendem este item. Substitui a cadeia de ~25
   * condicoes `||` que vivia em `getBackgroundBotaoMenu`: alem de ilegivel,
   * ela ja errava — `/mix-inteligente` nunca constou dela, entao o item do Mix
   * nunca acendia. Com o menu agrupado a regra ganhou um segundo uso: e ela que
   * decide qual SECAO abre sozinha na carga da tela.
   */
  matchers: string[]
}

/**
 * Uma secao do menu: um MODULO do produto (Predict, Mix Inteligente, Radar de
 * Churn) ou um agrupamento generico (Dados, Administracao).
 *
 * O Mix fica como secao mesmo tendo uma tela so — o nome do modulo e a
 * informacao que se quer dar, e um item solto entre as secoes nao diria a que
 * modulo pertence.
 */
type Secao = {
  id: string
  titulo: string
  icon: React.ReactNode
  itens: ItemMenu[]
  /**
   * Modulo anunciado mas sem tela acessivel: nao recolhe (nao ha o que
   * esconder) e o cabecalho ganha o selo e a explicacao.
   */
  indisponivel?: { badge: string; ajuda: string }
}

/**
 * `item` e um destino de PRIMEIRO NIVEL, fora de qualquer secao — hoje so o
 * Dashboard. Ele nao pertence a um modulo: e a visao geral da operacao, e
 * dentro de "Predict" ficava a um clique de distancia atras do acordeao (a
 * secao recolhida escondia a primeira tela que todo perfil abre).
 */
type Bloco =
  | { tipo: 'secao'; secao: Secao }
  | { tipo: 'item'; item: ItemMenu }
  | { tipo: 'divisor'; id: string }

// ---------------------------------------------------------------------------
// Itens (telas do tenant)
// ---------------------------------------------------------------------------

const DASHBOARD: ItemMenu = {
  url: '/dashboard',
  icon: <PieTwoIcon />,
  text: 'Dashboard',
  matchers: ['dashboard', 'produto', 'cliente'],
}
const PREDICOES: ItemMenu = {
  url: '/predicoes',
  // Calendario: o 'chart-line' passou a identificar o MODULO Predict, e pai e
  // filho com o mesmo icone e exatamente a colisao que esta demanda desfez. A
  // tela responde QUANDO o cliente deve comprar de novo — a data e o assunto.
  icon: <CalendarIcon />,
  text: 'Predições',
  matchers: ['predicoes', 'predicao'],
}
const HISTORICO_BAIXA: ItemMenu = {
  url: '/historico-baixa',
  icon: <Undo />,
  text: 'Histórico de Baixas',
  matchers: ['historico-baixa'],
}
const PREDICT_ANALYTICS: ItemMenu = {
  // Icone de barras, e nao o `PieTwoIcon` do Dashboard: agora os dois sao
  // irmaos diretos dentro de Predict, e no menu colapsado eram identicos.
  url: '/predict-analytics',
  icon: <Status />,
  text: 'Predict Analytics',
  matchers: ['predict-analytics'],
}
const PAINEL_DO_MIX: ItemMenu = {
  // Icone proprio: usava o `ChartLineIcon` das Predicoes. O rotulo nao repete
  // "Mix Inteligente" porque o cabecalho da secao ja diz o modulo — e a tela e
  // o painel de gestao do Mix (as 4 abas), nao o modulo inteiro.
  url: '/mix-inteligente',
  icon: <AdProduct />,
  text: 'Painel do Mix',
  matchers: ['mix-inteligente'],
}
const CLIENTES: ItemMenu = {
  url: '/clientes',
  icon: <UserIcon />,
  text: 'Clientes',
  matchers: ['clientes'],
}
const PRODUTOS: ItemMenu = {
  url: '/produtos',
  icon: <FacialCleanserIcon />,
  text: 'Produtos',
  matchers: ['produtos'],
}
const VENDEDORES: ItemMenu = {
  url: '/vendedores',
  icon: <EveryUser />,
  text: 'Vendedores',
  matchers: ['vendedores'],
}
const EMPRESAS: ItemMenu = {
  url: '/empresas',
  icon: <IconCity />,
  text: 'Empresas',
  matchers: ['empresas'],
}
const VENDAS: ItemMenu = {
  url: '/vendas',
  icon: <IconDollar />,
  text: 'Vendas',
  matchers: ['vendas'],
}
const MENSAGENS: ItemMenu = {
  url: '/historico-mensagens',
  icon: <MessagesIcon />,
  text: 'Mensagens enviadas',
  matchers: ['historico-mensagens'],
}
const PLANILHAS: ItemMenu = {
  // A rota existia e o manual do cliente manda "ir em Planilhas", mas nao havia
  // item de menu nem link em tela nenhuma: so se chegava digitando a URL. As
  // telas de resultado entram nos matchers para o item nao apagar no meio do
  // proprio fluxo de importacao.
  url: '/planilhas',
  icon: <IconFiles />,
  text: 'Planilhas',
  matchers: ['planilhas', 'planilhas-error', 'planilhas-sucesso'],
}
const USUARIOS: ItemMenu = {
  url: '/usuarios&permissoes',
  icon: <IconCard />,
  text: 'Usuários e permissões',
  matchers: ['usuarios&permissoes', 'newUser'],
}
const CONFIGURACOES: ItemMenu = {
  url: '/configuracoes',
  icon: <SettingTwoIcon color={colors.white} />,
  text: 'Configurações',
  matchers: ['configuracoes'],
}

// ---------------------------------------------------------------------------
// Itens (ambiente do super-admin)
// ---------------------------------------------------------------------------

const ORGANIZACOES: ItemMenu = {
  url: '/adm-organizacao',
  icon: <Organization />,
  text: 'Organizações',
  matchers: ['adm-organizacao'],
}
const USUARIOS_ADM: ItemMenu = {
  url: '/adm-user',
  icon: <IconCard />,
  text: 'Usuários',
  matchers: ['adm-user', 'new-user-adm'],
}
const GESTAO_TENANTS: ItemMenu = {
  url: '/adm-gestao-tenants',
  icon: <PieTwoIcon />,
  text: 'Gestão de Tenants',
  matchers: ['adm-gestao-tenants'],
}
const CENTRAL_RESULTADOS: ItemMenu = {
  // Era o `ChartLineIcon` das Predicoes; a seta de crescimento diz melhor o que
  // a tela mostra (evolucao da carteira).
  url: '/adm-central-resultados',
  icon: <TrendingUpIcon />,
  text: 'Central de Resultados',
  matchers: ['adm-central-resultados'],
}

const RADAR_DE_CHURN: Secao = {
  id: 'radar-de-churn',
  titulo: 'Radar de Churn',
  icon: <ModuloRadarIcon />,
  itens: [],
  indisponivel: {
    badge: 'Em breve',
    ajuda: 'Radar de Churn — módulo em desenvolvimento, ainda indisponível.',
  },
}

const CHAVE_SECOES_ABERTAS = 'menuSecoesAbertas'

const ehItemAtivo = (item: ItemMenu, segmentos: string[]): boolean =>
  item.matchers.some(matcher => segmentos.includes(matcher))

/**
 * Secoes que o usuario deixou abertas, ou `null` enquanto ele nunca recolheu
 * nada. `null` NAO e "nenhuma aberta": e "todas abertas".
 *
 * A diferenca importa porque nem toda rota pertence a uma secao —
 * `/administrador`, `/editar&perfil` e `/organizacao` nao pertencem. Com o
 * padrao "tudo fechado", quem abrisse o app em `/administrador` (que e so uma
 * imagem de fundo, sem navegacao propria) veria apenas os cabecalhos e
 * NENHUM destino clicavel na tela inteira — o menu plano de antes sempre
 * mostrava os itens.
 */
const lerSecoesAbertas = (): string[] | null => {
  const bruto = localStorage.getItem(CHAVE_SECOES_ABERTAS)
  if (bruto === null) {
    return null
  }
  try {
    const valor = JSON.parse(bruto)
    return Array.isArray(valor)
      ? valor.filter(id => typeof id === 'string')
      : null
  } catch (erro) {
    return null
  }
}

/**
 * Menu do ambiente administrativo do super-admin. NAO segue os modulos do
 * produto de proposito: aqui nao se opera carteira nenhuma — sao as contas e o
 * acompanhamento delas.
 */
const montarBlocosAdministrativo = (ehSuperAdmin: boolean): Bloco[] => {
  const blocos: Bloco[] = [
    {
      tipo: 'secao',
      secao: {
        id: 'adm-cadastro',
        titulo: 'Cadastro',
        icon: <IconCity />,
        itens: [ORGANIZACOES, USUARIOS_ADM],
      },
    },
  ]
  // Gestao de Tenants e Central de Resultados sao do super-admin GLOBAL: o
  // backend responde 403 ao admin de tenant.
  if (ehSuperAdmin) {
    blocos.push({
      tipo: 'secao',
      secao: {
        id: 'adm-acompanhamento',
        titulo: 'Acompanhamento',
        icon: <Status />,
        itens: [GESTAO_TENANTS, CENTRAL_RESULTADOS],
      },
    })
  }
  return blocos
}

/**
 * Menu do tenant, organizado pelos MODULOS do produto (Predict, Mix
 * Inteligente, Radar de Churn) e, abaixo do divisor, pelas secoes genericas.
 *
 * A base de telas continua vindo de `papelOrganizacao` (PD-0004: assistente usa
 * as telas do vendedor; coordenador tem as de acompanhamento de equipe),
 * enquanto Predict Analytics e Mix seguem a regra propria deles — qualquer
 * perfil com vinculo no tenant, E a flag do modulo ligada para a organizacao.
 * Sao condicoes diferentes de proposito: um super-admin com vinculo de VENDEDOR
 * ve o menu de vendedor E os dois modulos, como ja era antes.
 */
const montarBlocosTenant = (
  papelOrganizacao: PapelOrganizacaoType | undefined,
  ehSuperAdmin: boolean,
  organizacaoCarregada: boolean,
  moduloPredictAnalyticsAtivo: boolean,
  moduloMixInteligenteAtivo: boolean
): Bloco[] => {
  const ehAdminDoTenant = papelOrganizacao === 'ADMINISTRADOR'
  const ehCoordenador = papelOrganizacao === 'COORDENADOR'
  const vePainelDeEquipe = ehAdminDoTenant || ehCoordenador
  // Os modulos deixaram de ser so-admin: todo perfil com vinculo os abre, com
  // os dados recortados pela propria carteira no backend (`EscopoVendedor`).
  // `papelOrganizacao` undefined e o super-admin sem vinculo neste tenant — que
  // o backend trata como irrestrito, e por isso entra pelo `ehSuperAdmin`.
  // `!= null` (e não `!== undefined`) para casar exatamente com o gate de `routes.tsx`: se a
  // API algum dia devolver `papelOrganizacao: null`, escrituras diferentes fariam o menu
  // exibir um item cuja rota não foi registrada — clique em tela branca.
  const veModulos = ehSuperAdmin || papelOrganizacao != null

  const predict: ItemMenu[] = [PREDICOES]
  // Historico de Baixas era so do painel de equipe; passou a valer para
  // vendedor e assistente tambem (o backend ja recortava por escopo).
  predict.push(HISTORICO_BAIXA)
  if (veModulos && moduloPredictAnalyticsAtivo) {
    predict.push(PREDICT_ANALYTICS)
  }

  const blocos: Bloco[] = [
    // Primeiro da lista e fora das secoes: e a tela de abertura do produto (o
    // logo do menu aponta para ela) e vale para todos os perfis.
    { tipo: 'item', item: DASHBOARD },
    {
      tipo: 'secao',
      secao: {
        id: 'predict',
        titulo: 'Predict',
        icon: <ChartLineIcon />,
        itens: predict,
      },
    },
  ]

  // O Mix tem uma unica tela propria (o painel), agora aberta a todos os
  // perfis com vinculo — antes so ao palco admin. O vendedor continua
  // consumindo o modulo tambem de dentro das telas do Predict (recomendacoes no
  // detalhe da predicao, oportunidades no pedido).
  // `organizacaoCarregada` ja subsume a flag: ela so pode ser true se o /me
  // respondeu.
  //
  // ⚠️ Com o modulo DESLIGADO a secao vira convite comercial, e ai o publico e
  // outro: so admin do tenant e super-admin. Abrir o painel a todos os perfis
  // arrastaria junto o "Fale com a Linkcom" para quem NAO decide contratacao —
  // uma porta que nao abre, e um chamado de "como eu contrato isto?". Decisao
  // de produto (2026-09-10), nao consequencia da liberacao do painel.
  const veConviteDoMix = ehSuperAdmin || ehAdminDoTenant
  if (
    organizacaoCarregada &&
    (moduloMixInteligenteAtivo ? veModulos : veConviteDoMix)
  ) {
    blocos.push({
      tipo: 'secao',
      secao: {
        id: 'mix',
        titulo: 'Mix Inteligente',
        icon: <ModuloMixIcon />,
        itens: moduloMixInteligenteAtivo ? [PAINEL_DO_MIX] : [],
        // Mesmo gesto do `MixChamariz` no detalhe da predicao: com o modulo
        // desligado o produto convida a conhece-lo em vez de esconde-lo. So
        // depois do /me responder (`organizacaoCarregada`), porque o valor
        // NEGADO da flag nao vale antes disso — senao o convite apareceria a
        // quem TEM o modulo, ate a resposta chegar.
        indisponivel: moduloMixInteligenteAtivo
          ? undefined
          : {
              badge: 'Conheça',
              ajuda:
                'O Mix Inteligente não está ativo nesta organização. Fale com a Linkcom para conhecer o módulo.',
            },
      },
    })
  }

  blocos.push({ tipo: 'secao', secao: RADAR_DE_CHURN })
  blocos.push({ tipo: 'divisor', id: 'divisor-modulos' })

  // Clientes e Produtos passaram a valer para todos os perfis (o escopo do
  // backend ja decidia o que cada um enxerga na lista). Vendedores e Empresas
  // continuam sendo acompanhamento de equipe: sao o cadastro de QUEM vende e de
  // ONDE se vende, nao a carteira de quem atende.
  const dados: ItemMenu[] = [CLIENTES, PRODUTOS]
  if (vePainelDeEquipe) {
    dados.push(VENDEDORES, EMPRESAS)
  }
  dados.push(VENDAS, MENSAGENS)
  blocos.push({
    tipo: 'secao',
    secao: {
      id: 'dados',
      titulo: 'Dados',
      icon: <SecaoDadosIcon />,
      itens: dados,
    },
  })

  if (ehAdminDoTenant) {
    blocos.push({
      tipo: 'secao',
      secao: {
        id: 'administracao',
        titulo: 'Administração',
        icon: <SecaoAdministracaoIcon />,
        itens: [PLANILHAS, USUARIOS, CONFIGURACOES],
      },
    })
  }

  return blocos
}

export function Menu({ isBigMenu }: MenuProps) {
  const { userDataOrganization, userData } = useTopUserData()
  const { pathname } = useLocation()
  const [secoesAbertas, setSecoesAbertas] = useState<string[] | null>(
    lerSecoesAbertas
  )

  const segmentos = pathname.split('/')
  const ehSuperAdmin = userData?.papel === 'ADMINISTRADOR'
  const ehAdministrativo = ehRotaAdministrativa(pathname)

  // Mesma regra de prefixo do logo e do Top: a lista fixa que estava aqui nunca
  // recebeu 'adm-central-resultados', entao o menu do super-admin virava o menu
  // do TENANT justamente dentro da Central de Resultados.
  const blocos = ehAdministrativo
    ? montarBlocosAdministrativo(ehSuperAdmin)
    : montarBlocosTenant(
        userDataOrganization?.papelOrganizacao,
        ehSuperAdmin,
        userDataOrganization !== undefined,
        Boolean(userDataOrganization?.moduloPredictAnalyticsAtivo),
        Boolean(userDataOrganization?.moduloMixInteligenteAtivo)
      )

  const blocoAtivo = blocos.find(
    bloco =>
      bloco.tipo === 'secao' &&
      bloco.secao.itens.some(item => ehItemAtivo(item, segmentos))
  )
  const idSecaoAtiva =
    blocoAtivo !== undefined && blocoAtivo.tipo === 'secao'
      ? blocoAtivo.secao.id
      : null

  // A secao da rota atual abre sozinha, senao o item ativo ficaria escondido.
  // O efeito NAO grava no localStorage — so o clique no cabecalho e memorizado.
  // (O `LayoutPage` fica DENTRO de cada rota, entao o Menu remonta a cada
  // navegacao e isto roda de novo: recolher a secao ativa nao sobrevive a uma
  // troca de tela, o que e o comportamento desejado.)
  //
  // Nas rotas do Dashboard (`/dashboard`, os detalhes sob ele e o legado
  // `/cliente/:id`) NENHUMA secao abre: o item ativo e o de primeiro nivel, que
  // nao mora em secao alguma e ja esta sempre visivel. Abrir "Predict" ali
  // seria abrir uma secao que nao contem a tela aberta.
  useEffect(() => {
    if (idSecaoAtiva === null) {
      return
    }
    setSecoesAbertas(anteriores => {
      if (anteriores === null || anteriores.includes(idSecaoAtiva)) {
        return anteriores
      }
      return [...anteriores, idSecaoAtiva]
    })
  }, [idSecaoAtiva])

  const secaoEstaAberta = (id: string): boolean =>
    secoesAbertas === null || secoesAbertas.includes(id)

  const alternarSecao = (id: string) => {
    // Primeiro clique materializa o estado implicito "todas abertas" na lista
    // real de secoes deste perfil — senao recolher uma sozinha abriria todas as
    // outras de volta ao gravar.
    const base =
      secoesAbertas ??
      blocos
        .filter(bloco => bloco.tipo === 'secao')
        .map(bloco => (bloco.tipo === 'secao' ? bloco.secao.id : ''))
    const proximas = base.includes(id)
      ? base.filter(aberta => aberta !== id)
      : [...base, id]
    setSecoesAbertas(proximas)
    localStorage.setItem(CHAVE_SECOES_ABERTAS, JSON.stringify(proximas))
  }

  const renderLogoLink = (to: string) => (
    <LogoMenu isBigMenu={isBigMenu}>
      <LinkStyled background="" to={to}>
        {/* 23px no colapsado: acima disso o simbolo (1.8em) estoura os 42px
            de altura do LinkStyled. */}
        <LogoCartivia
          somenteSimbolo={!isBigMenu}
          tamanho={isBigMenu ? 22 : 23}
        />
      </LinkStyled>
    </LogoMenu>
  )

  const renderItem = (item: ItemMenu, aninhado: boolean) => (
    <LinkStyled
      background={ehItemAtivo(item, segmentos) ? colors.azulOpaco : ''}
      $isBigMenu={isBigMenu}
      $aninhado={aninhado}
      to={item.url}
      key={item.url}
      // Colapsado o icone e a unica informacao do item; sem isto nao havia como
      // descobrir o destino a nao ser clicando.
      title={isBigMenu ? undefined : item.text}
    >
      <IconeMenu>{item.icon}</IconeMenu>
      {isBigMenu ? item.text : null}
    </LinkStyled>
  )

  const renderSecao = (secao: Secao) => {
    // Secao sem nenhuma tela acessivel (Radar de Churn, e o Mix de quem nao
    // contratou o modulo) nao recolhe: nao ha o que esconder, e um botao que
    // nao faz nada prometeria um clique que nao existe.
    if (secao.itens.length === 0) {
      return (
        <SecaoRotuloStyle
          key={secao.id}
          $ativa={false}
          $indisponivel
          aria-disabled="true"
          title={secao.indisponivel?.ajuda}
        >
          <IconeMenu>{secao.icon}</IconeMenu>
          {secao.titulo}
          {secao.indisponivel !== undefined ? (
            <BadgeModulo>{secao.indisponivel.badge}</BadgeModulo>
          ) : null}
        </SecaoRotuloStyle>
      )
    }
    const aberta = secaoEstaAberta(secao.id)
    return (
      <Fragment key={secao.id}>
        <SecaoTituloStyle
          type="button"
          $ativa={secao.id === idSecaoAtiva}
          aria-expanded={aberta}
          onClick={() => alternarSecao(secao.id)}
        >
          <IconeMenu>{secao.icon}</IconeMenu>
          {secao.titulo}
          <SetaSecao $aberta={aberta}>
            <ChevronDownIcon />
          </SetaSecao>
        </SecaoTituloStyle>
        {aberta ? secao.itens.map(item => renderItem(item, true)) : null}
      </Fragment>
    )
  }

  /**
   * Colapsado (100px) cabe so o icone: cabecalho de secao nao tem onde escrever
   * o rotulo e um acordeao sem titulo nao se explica. Entao a barra estreita
   * segue plana — como sempre foi —, com o icone do modulo indisponivel no
   * lugar dos itens que ele ainda nao tem. Evita tambem construir um flyout de
   * hover, que nao tem equivalente em toque.
   */
  const renderNavegacao = () => {
    if (isBigMenu) {
      return blocos.map(bloco => {
        if (bloco.tipo === 'secao') {
          return renderSecao(bloco.secao)
        }
        if (bloco.tipo === 'item') {
          // `aninhado: false` — alinha com os cabecalhos de secao, e nao com os
          // itens que vivem dentro delas: e um destino de primeiro nivel.
          return renderItem(bloco.item, false)
        }
        return <DivisorMenu key={bloco.id} />
      })
    }
    return blocos.map(bloco => {
      if (bloco.tipo === 'divisor') {
        return null
      }
      if (bloco.tipo === 'item') {
        return renderItem(bloco.item, false)
      }
      const { secao } = bloco
      if (secao.itens.length === 0) {
        return (
          <ItemBloqueado
            key={secao.id}
            aria-disabled="true"
            aria-label={secao.titulo}
            title={secao.indisponivel?.ajuda ?? secao.titulo}
          >
            <IconeMenu>{secao.icon}</IconeMenu>
          </ItemBloqueado>
        )
      }
      return (
        <Fragment key={secao.id}>
          {secao.itens.map(item => renderItem(item, false))}
        </Fragment>
      )
    })
  }

  return (
    <MenuStyle reduceMenu={!isBigMenu}>
      {renderLogoLink(ehAdministrativo ? '/administrador' : '/dashboard')}
      <NavegacaoStyle aria-label="Navegação principal">
        {renderNavegacao()}
      </NavegacaoStyle>
      <ContainerItems isBigMenu={isBigMenu} />
    </MenuStyle>
  )
}
